import { useState, useCallback, useEffect, useRef } from 'react';
import { Search, RefreshCw, RotateCcw, AlertCircle } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { DateRangePicker } from '../../ui/date-picker';
import { cn } from '../../../lib/utils';
import type {
  RepescagemLead,
  RepescagemLeadDetail,
  KanbanColumns,
  KanbanCounts,
  SyncStatus,
  RepescagemStage,
} from './types';
import {
  STAGE_CONFIG,
  STAGE_ORDER,
  formatDateTime,
} from './types';
import { RepescagemKanbanColumn } from './RepescagemKanbanColumn';
import { RepescagemDetailDrawer } from './RepescagemDetailDrawer';
import type { AttemptFormData } from './RepescagemAttemptDialog';

interface RepescagemPageProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
}

const EMPTY_COLUMNS: KanbanColumns = {
  LEAD: [],
  TENTATIVA: [],
  SEM_RETORNO: [],
  DECLINADO: [],
  INTERESSADO: [],
};

const EMPTY_COUNTS: KanbanCounts = {
  LEAD: 0,
  TENTATIVA: 0,
  SEM_RETORNO: 0,
  DECLINADO: 0,
  INTERESSADO: 0,
};

async function apiFetch(
  url: string,
  options: RequestInit,
  accessToken: string | undefined,
  attemptRefresh: () => Promise<boolean>,
  refreshToken: string | undefined
): Promise<Response> {
  const buildHeaders = (token: string | undefined): Record<string, string> => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string> | undefined),
  });

  const res = await fetch(url, { ...options, headers: buildHeaders(accessToken) });

  if (res.status === 401 && refreshToken) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      // Read fresh token from localStorage after refresh
      const freshToken = localStorage.getItem('mt-backoffice:access-token');
      const retryRes = await fetch(url, { ...options, headers: buildHeaders(freshToken ?? undefined) });
      return retryRes;
    }
  }

  return res;
}

export function RepescagemPage({
  accessToken,
  refreshToken,
  attemptRefresh,
}: RepescagemPageProps) {
  const [columns, setColumns] = useState<KanbanColumns>(EMPTY_COLUMNS);
  const [counts, setCounts] = useState<KanbanCounts>(EMPTY_COUNTS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimerRef = useRef<number | null>(null);
  const searchRef = useRef(''); // Stable ref to avoid re-render cascades

  const [dateRange, setDateRange] = useState<{from: Date | undefined; to: Date | undefined} | undefined>(undefined);
  const dateRangeRef = useRef<{from: Date | undefined; to: Date | undefined} | undefined>(undefined);

  const [selectedLead, setSelectedLead] = useState<RepescagemLeadDetail | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const syncPollRef = useRef<number | null>(null);

  const [isSubmittingAttempt, setIsSubmittingAttempt] = useState(false);

  const [quoteDates, setQuoteDates] = useState<Date[]>([]);

  // Backend wraps all 2xx responses in { success: true, data: <actual> }
  // This unwraps to return the actual payload.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const unwrap = (body: any) => (body && 'data' in body ? body.data : body);

  // --- Data fetching ---

  const fetchKanban = useCallback(
    async (searchTerm = '', range?: {from: Date | undefined; to: Date | undefined}) => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (searchTerm) params.append('search', searchTerm);
        if (range?.from) {
          params.append('startDate', range.from.toISOString().slice(0, 10));
        }
        if (range?.to) {
          params.append('endDate', range.to.toISOString().slice(0, 10));
        }
        const qs = params.toString();
        const url = `/v1/repescagem/kanban${qs ? '?' + qs : ''}`;
        const res = await apiFetch(
          url,
          { method: 'GET' },
          accessToken,
          attemptRefresh,
          refreshToken
        );

        if (!res.ok) {
          const payload = await res.json().catch(() => ({}));
          throw new Error(payload?.error?.message ?? `Erro ao carregar kanban (${res.status})`);
        }

        const data = unwrap(await res.json());
        setColumns(data?.columns ?? EMPTY_COLUMNS);
        setCounts(data?.counts ?? EMPTY_COUNTS);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro desconhecido');
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken, attemptRefresh, refreshToken]
  );

  const startSyncPolling = useCallback(() => {
    if (syncPollRef.current !== null) return; // Already polling
    setIsSyncing(true);

    const pollOnce = async () => {
      try {
        const pollRes = await apiFetch(
          '/v1/repescagem/sync/status',
          { method: 'GET' },
          accessToken,
          attemptRefresh,
          refreshToken
        );
        if (!pollRes.ok) return;
        const pollData: SyncStatus = unwrap(await pollRes.json());
        setSyncStatus(pollData);
        if (pollData.status !== 'RUNNING') {
          if (syncPollRef.current !== null) {
            window.clearInterval(syncPollRef.current);
            syncPollRef.current = null;
          }
          setIsSyncing(false);
          void fetchKanban(searchRef.current, dateRangeRef.current);
          void fetchQuoteDates();
        }
      } catch {
        // Ignore transient polling errors
      }
    };

    // Immediate first poll, then every 3 seconds
    void pollOnce();
    syncPollRef.current = window.setInterval(pollOnce, 3000);
  }, [accessToken, attemptRefresh, refreshToken, fetchKanban]);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const res = await apiFetch(
        '/v1/repescagem/sync/status',
        { method: 'GET' },
        accessToken,
        attemptRefresh,
        refreshToken
      );
      if (res.ok) {
        const data: SyncStatus = unwrap(await res.json());
        setSyncStatus(data);
        // Auto-detect a running sync (e.g. cron job or another user triggered it)
        if (data?.status === 'RUNNING') {
          startSyncPolling();
        }
      }
    } catch {
      // Ignore sync status errors
    }
  }, [accessToken, attemptRefresh, refreshToken, startSyncPolling]);

  const fetchQuoteDates = useCallback(async () => {
    try {
      const res = await apiFetch(
        '/v1/repescagem/kanban/dates',
        { method: 'GET' },
        accessToken,
        attemptRefresh,
        refreshToken
      );
      if (res.ok) {
        const data: string[] = unwrap(await res.json());
        if (Array.isArray(data)) {
          setQuoteDates(data.map(d => new Date(d + 'T12:00:00'))); // noon to avoid timezone shifts
        }
      }
    } catch {
      // Ignore errors
    }
  }, [accessToken, attemptRefresh, refreshToken]);

  const fetchLeadDetail = useCallback(
    async (leadId: string) => {
      setIsLoadingDetail(true);
      try {
        const res = await apiFetch(
          `/v1/repescagem/leads/${leadId}`,
          { method: 'GET' },
          accessToken,
          attemptRefresh,
          refreshToken
        );
        if (!res.ok) {
          const payload = await res.json().catch(() => ({}));
          throw new Error(payload?.error?.message ?? 'Erro ao carregar detalhes');
        }
        const data = unwrap(await res.json());
        setSelectedLead(data);
        setIsDrawerOpen(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar detalhes');
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [accessToken, attemptRefresh, refreshToken]
  );

  const stopSyncPolling = useCallback(() => {
    if (syncPollRef.current !== null) {
      window.clearInterval(syncPollRef.current);
      syncPollRef.current = null;
    }
  }, []);

  // --- Effects ---

  useEffect(() => {
    void fetchKanban();
    void fetchSyncStatus();
    void fetchQuoteDates();
  }, [fetchKanban, fetchSyncStatus]);

  // Debounced search
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (searchTimerRef.current !== null) {
        window.clearTimeout(searchTimerRef.current);
      }
      searchTimerRef.current = window.setTimeout(() => {
        setSearch(value);
        searchRef.current = value;
        void fetchKanban(value, dateRangeRef.current);
      }, 400);
    },
    [fetchKanban]
  );

  // Refetch when dateRange changes
  useEffect(() => {
    dateRangeRef.current = dateRange;
    void fetchKanban(search, dateRange);
  }, [dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup search timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current !== null) {
        window.clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  // Cleanup sync polling interval on unmount
  useEffect(() => {
    return () => {
      stopSyncPolling();
    };
  }, [stopSyncPolling]);

  // --- Handlers ---

  const handleCardClick = useCallback(
    (lead: RepescagemLead) => {
      void fetchLeadDetail(lead.id);
    },
    [fetchLeadDetail]
  );

  const handleSync = useCallback(async () => {
    // Stop any existing polling before starting a new sync
    stopSyncPolling();
    setIsSyncing(true);

    try {
      const res = await apiFetch(
        '/v1/repescagem/sync',
        { method: 'POST' },
        accessToken,
        attemptRefresh,
        refreshToken
      );
      if (!res.ok) {
        setIsSyncing(false);
        return;
      }

      // Always start polling after triggering sync — polling will stop when done
      startSyncPolling();
    } catch {
      setIsSyncing(false);
    }
  }, [accessToken, attemptRefresh, refreshToken, stopSyncPolling, startSyncPolling]);

  const handleAddAttempt = useCallback(
    async (leadId: string, data: AttemptFormData) => {
      setIsSubmittingAttempt(true);
      try {
        const res = await apiFetch(
          `/v1/repescagem/leads/${leadId}/attempts`,
          {
            method: 'POST',
            body: JSON.stringify(data),
          },
          accessToken,
          attemptRefresh,
          refreshToken
        );
        if (!res.ok) {
          const payload = await res.json().catch(() => ({}));
          throw new Error(payload?.error?.message ?? 'Erro ao registrar tentativa');
        }
        // Refresh detail and kanban
        await fetchLeadDetail(leadId);
        await fetchKanban(search, dateRange);
      } finally {
        setIsSubmittingAttempt(false);
      }
    },
    [accessToken, attemptRefresh, refreshToken, fetchLeadDetail, fetchKanban, search, dateRange]
  );

  const handleChangeStage = useCallback(
    async (leadId: string, stage: RepescagemStage, notes?: string) => {
      const res = await apiFetch(
        `/v1/repescagem/leads/${leadId}/stage`,
        {
          method: 'PATCH',
          body: JSON.stringify({ stage, notes: notes ?? null }),
        },
        accessToken,
        attemptRefresh,
        refreshToken
      );
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.error?.message ?? 'Erro ao mudar etapa');
      }
      // Refresh detail and kanban
      await fetchLeadDetail(leadId);
      await fetchKanban(search, dateRange);
    },
    [accessToken, attemptRefresh, refreshToken, fetchLeadDetail, fetchKanban, search, dateRange]
  );

  const handleUpdateNotes = useCallback(
    async (leadId: string, notes: string) => {
      const res = await apiFetch(
        `/v1/repescagem/leads/${leadId}/notes`,
        {
          method: 'PATCH',
          body: JSON.stringify({ notes }),
        },
        accessToken,
        attemptRefresh,
        refreshToken
      );
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.error?.message ?? 'Erro ao salvar notas');
      }
      // Update the selected lead notes locally without full re-fetch
      setSelectedLead((prev) => (prev ? { ...prev, generalNotes: notes } : prev));
    },
    [accessToken, attemptRefresh, refreshToken]
  );

  const handleDrawerClose = useCallback((isOpen: boolean) => {
    setIsDrawerOpen(isOpen);
    if (!isOpen) {
      setSelectedLead(null);
    }
  }, []);

  const totalCount = Object.values(counts).reduce((sum, c) => sum + c, 0);

  return (
    <div className="flex flex-col h-full">
      {/* Page header */}
      <div className="flex-none px-4 py-4 border-b bg-background">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div>
            <h1 className="text-lg font-semibold text-foreground">Repescagem de Leads</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {totalCount > 0 ? `${totalCount} leads no funil` : 'Carregando...'}
              {syncStatus?.lastSyncAt && (
                <span className="ml-2">
                  · Última sync: {formatDateTime(syncStatus.lastSyncAt)}
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => fetchKanban(search, dateRange)}
              disabled={isLoading}
              className="h-8"
            >
              <RotateCcw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
              <span className="sr-only">Atualizar</span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleSync}
              disabled={isSyncing}
              className="h-8"
            >
              <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', isSyncing && 'animate-spin')} />
              {isSyncing ? 'Sincronizando...' : 'Sincronizar'}
            </Button>
          </div>
        </div>

        {/* Search + stats row */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              type="search"
              placeholder="Buscar por número, cliente, contato..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>

          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            placeholder="Filtrar por data"
            className="w-full sm:w-auto"
            highlightedDates={quoteDates}
          />

          {/* Stats badges */}
          <div className="hidden md:flex items-center gap-2">
            {STAGE_ORDER.map((stage) => {
              const cfg = STAGE_CONFIG[stage];
              const count = counts[stage];
              return (
                <span
                  key={stage}
                  className={cn(
                    'inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border',
                    cfg.color
                  )}
                >
                  {cfg.label}
                  <span className="font-bold">{count}</span>
                </span>
              );
            })}
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="flex-none px-4 py-3 bg-destructive/10 border-b border-destructive/20">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto h-6 text-xs"
              onClick={() => fetchKanban(search, dateRange)}
            >
              Tentar novamente
            </Button>
          </div>
        </div>
      )}

      {/* Sync progress banner */}
      {isSyncing && syncStatus && (
        <div className="flex-none px-4 py-3 bg-blue-500/10 border-b border-blue-500/20">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2 text-sm text-blue-700 dark:text-blue-400">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 flex-shrink-0 animate-spin" />
                <span className="font-medium">
                  {syncStatus.totalToProcess && syncStatus.totalToProcess > 0
                    ? `Sincronizando... ${syncStatus.imported + syncStatus.skipped + syncStatus.errors} de ${syncStatus.totalToProcess} processados`
                    : 'Sincronizando dados do Conta Azul...'}
                </span>
              </div>
              <span className="text-xs text-blue-600/80 dark:text-blue-300/80 whitespace-nowrap">
                {syncStatus.imported} importados · {syncStatus.skipped} ignorados
                {syncStatus.errors > 0 && (
                  <span className="text-amber-600 dark:text-amber-400"> · {syncStatus.errors} erros</span>
                )}
              </span>
            </div>

            {syncStatus.totalToProcess && syncStatus.totalToProcess > 0 && (
              <div className="w-full h-1.5 bg-blue-500/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(
                      100,
                      Math.round(
                        ((syncStatus.imported + syncStatus.skipped + syncStatus.errors) /
                          syncStatus.totalToProcess) *
                          100
                      )
                    )}%`,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Kanban board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        {isLoading && totalCount === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3 text-muted-foreground">
              <RotateCcw className="h-8 w-8 animate-spin" />
              <p className="text-sm">Carregando leads...</p>
            </div>
          </div>
        ) : (
          <div className="flex gap-3 p-4 h-full min-w-max">
            {STAGE_ORDER.map((stage) => (
              <RepescagemKanbanColumn
                key={stage}
                stage={stage}
                config={STAGE_CONFIG[stage]}
                leads={columns[stage] ?? []}
                count={counts[stage] ?? 0}
                onCardClick={handleCardClick}
              />
            ))}
          </div>
        )}
      </div>

      {/* Detail drawer */}
      <RepescagemDetailDrawer
        lead={selectedLead}
        open={isDrawerOpen}
        onOpenChange={handleDrawerClose}
        onAddAttempt={handleAddAttempt}
        onChangeStage={handleChangeStage}
        onUpdateNotes={handleUpdateNotes}
        isSubmittingAttempt={isSubmittingAttempt}
      />

      {/* Loading overlay for card detail */}
      {isLoadingDetail && (
        <div className="fixed inset-0 z-40 bg-background/40 flex items-center justify-center pointer-events-none">
          <div className="bg-background border rounded-lg px-6 py-4 shadow-lg flex items-center gap-3">
            <RotateCcw className="h-4 w-4 animate-spin text-primary" />
            <span className="text-sm">Carregando detalhes...</span>
          </div>
        </div>
      )}
    </div>
  );
}
